import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { fromPromise } from 'xstate';

import { useNoticeBannersStackContext } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';

import {
  type LockGroupOrderErrorParams,
  type LockGroupOrderParams,
  type LockGroupOrderResponse,
  type OrderingMachineAction,
} from '@order/features-ordering';

import {
  type BagLockGroupOrderMutation,
  useBagLockGroupOrderMutation,
} from '../../GraphQL/BagGroupOrder.generated';
import { prepareCart } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useLockGroupOrder = () => {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const [, executeLockGroupOrder] = useBagLockGroupOrderMutation();

  // ─── Derived ──────────────────────────────────────────────────────────────

  const lockGroupOrder = useMemo(
    () =>
      fromPromise<LockGroupOrderResponse, LockGroupOrderParams>(async ({ input }) => {
        const { reason } = input;
        const response = await executeLockGroupOrder({ input: { reason } });
        const result = response.data?.lockOrder;
        const isSuccessful = result?.__typename === 'LockOrderSuccess';

        if (isSuccessful) {
          return { cart: prepareCart(result.order as Partial<Order>) };
        }

        const responseError = getResponseError({ result }) ?? response.error?.message;

        throw new Error(responseError);
      }),
    [executeLockGroupOrder],
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleLockGroupOrderError = useCallback<OrderingMachineAction<LockGroupOrderErrorParams>>(
    (_args, params) => {
      addNoticeBanner({
        text: params.errorMessage || formatMessage(messages.lockGroupOrderError),
        palette: 'caution',
      });
    },
    [addNoticeBanner, formatMessage],
  );

  return { lockGroupOrder, handleLockGroupOrderError };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getResponseError(params: GetResponseErrorParams) {
  const { result } = params;

  if (!result || result.__typename === 'LockOrderSuccess') return;

  return result.errorMessage;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  lockGroupOrderError: {
    defaultMessage: 'We couldn’t lock the group order. Please try again.',
    description: 'Bag | Group order | Lock error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GetResponseErrorParams = {
  result: BagLockGroupOrderMutation['lockOrder'] | undefined;
};
